import { useEffect, useState } from 'react'

/**
 * Track a DOM element's bounding rect for guided-tour spotlight overlays.
 *
 * Originally lived inline in FirstLoginTour.tsx (Wave 9 Sujet 2.7,
 * Mustapha 2026-04-27). Extracted so the upcoming PostPaymentTour can
 * spotlight elements with the same multi-viewport robustness without
 * copy-pasting the measurement logic.
 *
 * Behavior:
 *   - Pass `null` to disable (returns null rect, no listeners attached).
 *   - When the selector matches multiple nodes (mobile + desktop nav
 *     duplicate `data-tour` attrs), the first node with a non-zero rect
 *     wins. Hidden duplicates collapse to (0, 0) and are skipped.
 *   - Re-measures on window resize, capture-phase scroll, ResizeObserver
 *     for size changes on existing matches, AND a MutationObserver on
 *     document.body so newly-mounted matches (e.g. after a Suspense
 *     fallback unblocks a lazy-loaded page) trigger a re-measure
 *     automatically.
 *
 * Why MutationObserver: the PostPaymentTour spans 4 routes and several
 * pages are lazy-loaded via React.lazy + Suspense. When the tour
 * navigates from /dashboard to /betternotes, the new sidebar item with
 * `[data-tour="bn-sidebar-library"]` doesn't exist at the moment the
 * effect's initial measure() runs. Without the MutationObserver the
 * rect would stay null forever (Codex 2026-04-28: "ResizeObserver only
 * watches existing elements; doesn't fire when NEW matching elements
 * mount later"). Polling was considered and rejected — arbitrary timing,
 * still brittle, plus the previous 250ms polling interval was visible
 * as spotlight lag on mobile/tablet.
 */
export function useSpotlightRect(selector: string | null): DOMRect | null {
  const [rect, setRect] = useState<DOMRect | null>(null)

  useEffect(() => {
    if (!selector) {
      // No-op when disabled. Callers that conditionally render the
      // spotlight (e.g. tours that branch on `activeSelector`) won't
      // read this stale rect, so we skip the setState to satisfy
      // react-hooks/set-state-in-effect.
      return
    }

    let observedEls: HTMLElement[] = []
    const ro = new ResizeObserver(() => measure())

    function measure() {
      if (!selector) return
      const els = Array.from(
        document.querySelectorAll(selector) as NodeListOf<HTMLElement>,
      )
      let chosen: DOMRect | null = null
      for (const el of els) {
        const r = el.getBoundingClientRect()
        if (r.width > 0 && r.height > 0) {
          chosen = r
          break
        }
      }
      setRect(chosen)

      // Re-target the ResizeObserver if the matched element set
      // changed (a Suspense fallback cleared, a route swapped a tab
      // selection, etc.). Disconnect + re-observe is cheap; this
      // keeps the size-change tracking in lock-step with the live
      // DOM without leaking observers.
      const sameSet =
        els.length === observedEls.length &&
        els.every((el, i) => el === observedEls[i])
      if (!sameSet) {
        ro.disconnect()
        for (const el of els) ro.observe(el)
        observedEls = els
      }
    }

    measure()

    const mo = new MutationObserver(() => measure())
    mo.observe(document.body, { childList: true, subtree: true })
    window.addEventListener('resize', measure, { passive: true })
    window.addEventListener('scroll', measure, { passive: true, capture: true })

    return () => {
      mo.disconnect()
      ro.disconnect()
      window.removeEventListener('resize', measure)
      window.removeEventListener('scroll', measure, true)
    }
  }, [selector])

  return rect
}
